import React, { useState, Fragment } from 'react';
import { LoanOfficerMetrics, FunnelStep } from '../../types/throughput';
import { Card, CardHeader, CardTitle } from '../ui/Card';
import { ChevronDown, ChevronUp, ChevronRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
interface CompanyAverages {
  claimed: number;
  converted: number;
  submittedToUW: number;
  ctc: number;
  funded: number;
  conversionRate: number;
  pullThrough: number;
  officerCount: number;
}
interface LoanOfficerTableProps {
  metrics: LoanOfficerMetrics[];
  companyAverages: CompanyAverages | null;
  onRowClick: (officerId: string) => void;
}
type SortField = 'name' | 'Created' | 'Converted' | 'Submitted to UW' | 'Closing Scheduled' | 'Funded' | 'conversion';
type SortDirection = 'asc' | 'desc';
type CoachingRow = {
  label: string;
  officerValue: number;
  companyAvg: number;
  format: 'number' | 'percent';
};
function getCoachingRows(m: LoanOfficerMetrics, avg: CompanyAverages): CoachingRow[] {
  return [{
    label: 'Claimed',
    officerValue: m.counts['Created'],
    companyAvg: avg.claimed,
    format: 'number'
  }, {
    label: 'Converted',
    officerValue: m.counts['Converted'],
    companyAvg: avg.converted,
    format: 'number'
  }, {
    label: 'Conv. Rate',
    officerValue: m.conversion.createdToConverted,
    companyAvg: avg.conversionRate,
    format: 'percent'
  }, {
    label: 'Sub. to UW',
    officerValue: m.counts['Submitted to UW'],
    companyAvg: avg.submittedToUW,
    format: 'number'
  }, {
    label: 'CTC',
    officerValue: m.counts['Closing Scheduled'],
    companyAvg: avg.ctc,
    format: 'number'
  }, {
    label: 'Funded',
    officerValue: m.counts['Funded'],
    companyAvg: avg.funded,
    format: 'number'
  }, {
    label: 'Pull-through',
    officerValue: m.conversion.createdToFunded,
    companyAvg: avg.pullThrough,
    format: 'percent'
  }];
}
function formatVal(value: number, fmt: 'number' | 'percent') {
  return fmt === 'percent' ? `${value.toFixed(1)}%` : value.toLocaleString();
}
function getDelta(officer: number, avg: number, fmt: 'number' | 'percent') {
  if (avg === 0) return {
    label: '—',
    direction: 'neutral' as const
  };
  const diff = officer - avg;
  const pctDiff = fmt === 'percent' ? diff : avg > 0 ? diff / avg * 100 : 0;
  const direction = pctDiff > 3 ? 'up' as const : pctDiff < -3 ? 'down' as const : 'neutral' as const;
  const sign = pctDiff > 0 ? '+' : '';
  const label = fmt === 'percent' ? `${sign}${pctDiff.toFixed(1)}pp` : `${sign}${Math.round(diff)}`;
  return {
    label,
    direction
  };
}
export function LoanOfficerTable({
  metrics,
  companyAverages,
  onRowClick
}: LoanOfficerTableProps) {
  const [sortField, setSortField] = useState<SortField>('Funded');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Auto-expand when filtered to a single officer with coaching data
  const autoExpandId = companyAverages && metrics.length === 1 ? metrics[0].officer.id : null;
  const effectiveExpandedId = autoExpandId ?? expandedId;
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedMetrics = [...metrics].sort((a, b) => {
    let valA: any;
    let valB: any;
    if (sortField === 'name') {
      valA = a.officer.name;
      valB = b.officer.name;
    } else if (sortField === 'conversion') {
      valA = a.conversion.createdToFunded;
      valB = b.conversion.createdToFunded;
    } else {
      valA = a.counts[sortField as FunnelStep];
      valB = b.counts[sortField as FunnelStep];
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <span className="w-3.5 h-3.5 inline-block opacity-0" data-id="element-83">·</span>;
    return sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-[#7C3AED]" data-id="element-84" /> : <ChevronDown className="w-3.5 h-3.5 text-[#7C3AED]" data-id="element-85" />;
  };
  const SortableHeader = ({
    field,
    children,
    className = ''
  }: {
    field: SortField;
    children: React.ReactNode;
    className?: string;
  }) => <th className={`px-6 py-3.5 font-medium text-[#706E6B] cursor-pointer hover:text-[#181818] transition-colors select-none ${className}`} onClick={() => handleSort(field)} data-id="element-86">
      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : ''}`} data-id="element-87">
        {children}
        <SortIcon field={field} data-id="element-88" />
      </div>
    </th>;
  const toggleExpand = (e: React.MouseEvent, officerId: string) => {
    e.stopPropagation();
    setExpandedId(expandedId === officerId ? null : officerId);
  };
  return <Card className="p-0 overflow-hidden" noPadding data-id="element-89">
      <CardHeader bordered className="!mb-0" data-id="element-90">
        <CardTitle data-id="element-91">Performance by Loan Officer</CardTitle>
        <span className="text-xs text-[#706E6B]" data-id="element-92">
          {sortedMetrics.length} officer{sortedMetrics.length !== 1 ? 's' : ''}
          {companyAverages && ' · Click arrow to compare vs avg'}
        </span>
      </CardHeader>

      <div className="overflow-x-auto" data-id="element-93">
        <table className="w-full text-sm" data-id="element-94">
          <thead className="text-xs uppercase tracking-wide bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-95">
            <tr data-id="element-96">
              {companyAverages && <th className="w-10 pl-4 pr-0 py-3.5" data-id="element-97" />}
              <SortableHeader field="name" data-id="element-98">Officer</SortableHeader>
              <SortableHeader field="Created" className="text-right" data-id="element-99">
                Claimed
              </SortableHeader>
              <SortableHeader field="Converted" className="text-right" data-id="element-100">
                Converted
              </SortableHeader>
              <SortableHeader field="Submitted to UW" className="text-right" data-id="element-101">
                Sub. to UW
              </SortableHeader>
              <SortableHeader field="Closing Scheduled" className="text-right" data-id="element-102">
                CTC
              </SortableHeader>
              <SortableHeader field="Funded" className="text-right" data-id="element-103">
                Funded
              </SortableHeader>
              <SortableHeader field="conversion" className="text-right" data-id="element-104">
                Pull-through
              </SortableHeader>
            </tr>
          </thead>
          <tbody data-id="element-105">
            {sortedMetrics.length === 0 ? <tr data-id="element-106">
                <td colSpan={companyAverages ? 8 : 7} className="px-6 py-12 text-center text-[#706E6B]" data-id="element-107">
                  No data available.
                </td>
              </tr> : sortedMetrics.map(row => {
            const isExpanded = effectiveExpandedId === row.officer.id;
            return <Fragment key={row.officer.id} data-id="element-108">
                    <tr className={`border-t border-[#E5E5E5] hover:bg-[#F5F3FF] transition-colors cursor-pointer group ${isExpanded ? 'bg-[#F5F3FF]' : 'bg-white'}`} onClick={() => onRowClick(row.officer.id)} data-id="element-109">
                      {companyAverages && <td className="pl-4 pr-0 py-3.5" data-id="element-110">
                          <button onClick={e => toggleExpand(e, row.officer.id)} className="w-6 h-6 rounded flex items-center justify-center text-[#706E6B] hover:text-[#7C3AED] hover:bg-[#7C3AED]/10 transition-colors" data-id="element-111">
                            <ChevronRight className={`h-4 w-4 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-112" />
                          </button>
                        </td>}
                      <td className="px-6 py-3.5" data-id="element-113">
                        <span className="font-medium text-[#181818] group-hover:text-[#7C3AED] transition-colors" data-id="element-114">
                          {row.officer.name}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-115">
                        {row.counts['Created'].toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-116">
                        {row.counts['Converted'].toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-117">
                        {row.counts['Submitted to UW'].toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-118">
                        {row.counts['Closing Scheduled'].toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right tabular-nums font-semibold text-[#181818]" data-id="element-119">
                        {row.counts['Funded'].toLocaleString()}
                      </td>
                      <td className="px-6 py-3.5 text-right tabular-nums text-[#706E6B]" data-id="element-120">
                        {row.conversion.createdToFunded.toFixed(1)}%
                      </td>
                    </tr>

                    {/* Coaching expansion row */}
                    <AnimatePresence data-id="element-121">
                      {isExpanded && companyAverages && <tr data-id="element-122">
                          <td colSpan={companyAverages ? 8 : 7} className="p-0" data-id="element-123">
                            <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: [0.4, 0, 0.2, 1]
                    }} className="overflow-hidden" data-id="element-124">
                              <div className="bg-[#FAFAFA] border-b border-[#E5E5E5] px-6 py-4" data-id="element-125">
                                <div className="flex items-center justify-between mb-3" data-id="element-126">
                                  <span className="text-xs font-medium text-[#706E6B] uppercase tracking-wide" data-id="element-127">
                                    vs Company Average (
                                    {companyAverages.officerCount} officers)
                                  </span>
                                </div>
                                <div className="grid grid-cols-7 gap-3" data-id="element-128">
                                  {getCoachingRows(row, companyAverages).map(cr => {
                            const delta = getDelta(cr.officerValue, cr.companyAvg, cr.format);
                            const DeltaIcon = delta.direction === 'up' ? TrendingUp : delta.direction === 'down' ? TrendingDown : Minus;
                            const deltaColor = delta.direction === 'up' ? 'text-[#04844B]' : delta.direction === 'down' ? 'text-[#EA001E]' : 'text-[#706E6B]';
                            const deltaBg = delta.direction === 'up' ? 'bg-[#04844B]/8' : delta.direction === 'down' ? 'bg-[#EA001E]/8' : 'bg-white';
                            return <div key={cr.label} className="bg-white rounded-lg border border-[#E5E5E5] px-3 py-3 text-center" data-id="element-129">
                                          <div className="text-[10px] font-medium uppercase tracking-wide text-[#706E6B] mb-2" data-id="element-130">
                                            {cr.label}
                                          </div>
                                          <div className="text-base font-semibold tabular-nums text-[#181818] mb-0.5" data-id="element-131">
                                            {formatVal(cr.officerValue, cr.format)}
                                          </div>
                                          <div className="text-[11px] text-[#706E6B] tabular-nums mb-2" data-id="element-132">
                                            Avg:{' '}
                                            {formatVal(cr.companyAvg, cr.format)}
                                          </div>
                                          <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${deltaColor} ${deltaBg}`} data-id="element-133">
                                            <DeltaIcon className="h-2.5 w-2.5" data-id="element-134" />
                                            {delta.label}
                                          </div>
                                        </div>;
                          })}
                                </div>
                              </div>
                            </motion.div>
                          </td>
                        </tr>}
                    </AnimatePresence>
                  </Fragment>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
}